import { defineStore } from "pinia";
import { ref, computed, watch } from "vue";
import { useAppState } from "./AppState";
import { useUserStore } from "./User";
import { useWorkspace } from "./Workspaces";
import {
    IMPORT_STEPS,
    IMPORT_TYPE_SOURCE_MAP,
    IMPORT_TYPES,
    getImportProgressPanelDestination,
    openImportProgressDestinationPanel,
} from "Admin/js/Constants/importWorkspace";
import { subscribeImportProgressChannel } from "Admin/js/Services/LiveSync/Channels/ImportProgressChannel.js";

export const useImportProgressStore = defineStore("importProgress", () => {
    const currentProgressId = ref(null);
    const progressData = ref(null);
    const progressLoading = ref(false);
    const progressError = ref(null);
    const showProgressDetailView = ref(false);
    const fetchingProgressId = ref(null);
    const userStore = useUserStore();

    const isImportFinished = computed(() =>
        ["completed", "error"].includes(progressData.value?.status),
    );

    async function fetchProgressDetail() {
        if (!currentProgressId.value) return;

        const targetProgressId = currentProgressId.value;

        // If already loading the same progressId, don't start a duplicate fetch
        if (
            progressLoading.value &&
            fetchingProgressId.value === targetProgressId
        ) {
            return;
        }

        // Allow fetching a different progressId even if a fetch is in progress
        // The previous fetch will be ignored when it completes

        try {
            fetchingProgressId.value = targetProgressId;
            progressLoading.value = true;
            progressError.value = null;

            const response = await fetch(
                `/api/import-progress/${targetProgressId}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRF-TOKEN":
                            document
                                .querySelector('meta[name="csrf-token"]')
                                ?.getAttribute("content") || "",
                    },
                },
            );

            // Check if the progressId changed during the fetch - if so, ignore this result
            if (currentProgressId.value !== targetProgressId) {
                return;
            }

            if (response.ok) {
                const data = await response.json();
                console.log("fetchProgressDetail: data", data);

                // Double-check the progressId hasn't changed after async operations
                if (currentProgressId.value !== targetProgressId) {
                    return;
                }

                if (data.success && data.progress) {
                    const mappedProgress = mapProgressData(data.progress);
                    progressData.value = mappedProgress;
                    showProgressDetailView.value = true;

                    // Update user store to keep it in sync
                    updateUserStoreProgress(mappedProgress);
                } else {
                    progressError.value = "Progress data not found";
                }
            } else {
                progressError.value = "Failed to fetch progress data";
            }
        } catch (err) {
            // Only set error if we're still fetching the same progressId
            if (currentProgressId.value === targetProgressId) {
                progressError.value = `Network error: ${err.message}`;
            }
        } finally {
            // Only clear loading if we're still fetching the same progressId
            if (currentProgressId.value === targetProgressId) {
                progressLoading.value = false;
                fetchingProgressId.value = null;
            }
        }
    }

    function mapProgressData(progress) {
        const isJsonImport = [
            IMPORT_TYPES.WORK_PANEL_IMPORT_JIRA_JSON,
            IMPORT_TYPES.WORK_PANEL_IMPORT_ASANA_JSON,
        ].includes(progress.type);

        const sourceLabel = IMPORT_TYPE_SOURCE_MAP[progress.type] || "";

        const importJobName = sourceLabel
            ? sourceLabel + (isJsonImport ? " Automation" : " Import")
            : null;

        const progressName = importJobName || progress.name || "Import Job";
        const typeName = sourceLabel
            ? sourceLabel + (isJsonImport ? " Automation" : "")
            : "";
        return {
            id: progress.id,
            name: progressName,
            status: progress.status,
            progress: progress.progress || 0,
            total: progress.meta?.item?.total || 0,
            imported: progress.meta?.item?.imported || 0,
            meta: progress.meta,
            created_at: progress.created_at,
            updated_at: progress.updated_at,
            type: progress.type,
            error: progress.error,
            typeName: typeName,
            counts: progress.meta?.counts || {
                boards: { total: 0, imported: 0 },
                items: { total: 0, imported: 0 },
                messages: { total: 0, imported: 0 },
                associates: { total: 0, imported: 0 },
            },
            filename: progress.filename ?? null,
        };
    }

    function updateUserStoreProgress(mappedProgress) {
        const progressId = mappedProgress.id;
        const initiatedIdx = userStore.initiated_progresses.findIndex(
            (p) => p.id === progressId,
        );
        const completedIdx = userStore.completed_progresses.findIndex(
            (p) => p.id === progressId,
        );

        // If status is completed or error, move to completed_progresses
        if (
            mappedProgress.status === "completed" ||
            mappedProgress.status === "error"
        ) {
            // Remove from initiated if present
            if (initiatedIdx !== -1) {
                userStore.initiated_progresses.splice(initiatedIdx, 1);
            }
            // Add to completed if not already there
            if (completedIdx === -1) {
                userStore.addCompletedProgress(mappedProgress);
            } else {
                // Update existing completed progress
                Object.assign(
                    userStore.completed_progresses[completedIdx],
                    mappedProgress,
                );
            }
        } else {
            // Update in initiated if present
            if (initiatedIdx !== -1) {
                Object.assign(
                    userStore.initiated_progresses[initiatedIdx],
                    mappedProgress,
                );
            } else if (completedIdx === -1) {
                // Add to initiated if not in either list
                userStore.addInitiatedProgress(mappedProgress);
            }
            // Remove from completed if it was there (status changed back)
            if (completedIdx !== -1) {
                userStore.completed_progresses.splice(completedIdx, 1);
                // Only add to initiated if it wasn't already there
                if (initiatedIdx === -1) {
                    userStore.addInitiatedProgress(mappedProgress);
                }
            }
        }
    }

    function setupWebSocket(progressId = currentProgressId.value) {
        if (!progressId) {
            return;
        }
        subscribeImportProgressChannel(progressId);
    }

    async function setProgressId(progressId) {
        currentProgressId.value = progressId;

        // Always show the progress detail view when opening a progress
        showProgressDetailView.value = true;

        // Set loading immediately so the loader is visible the moment the step
        // renders, before the async fetch completes.
        progressLoading.value = true;
        progressError.value = null;

        // If we already have this progress loaded, refresh it in background but still show the view
        const isSameProgressLoaded =
            progressData.value && progressData.value.id === progressId;
        if (isSameProgressLoaded) {
            // Refresh the data in the background
            await fetchProgressDetail();
            // Use currentProgressId.value to avoid race condition if progressId was changed during async operation
            if (currentProgressId.value === progressId) {
                setupWebSocket(currentProgressId.value);
            }
            return;
        }

        const reactiveProgress =
            userStore.initiated_progresses.find((p) => p.id === progressId) ||
            userStore.completed_progresses.find((p) => p.id === progressId) ||
            null;
        progressData.value = reactiveProgress;

        await fetchProgressDetail();
        // Use currentProgressId.value to avoid race condition if progressId was changed during async operation
        if (currentProgressId.value === progressId) {
            setupWebSocket(currentProgressId.value);
        }
    }

    watch(
        () => userStore.initiated_progresses,
        (list) => {
            if (!currentProgressId.value) return;
            const match = list.find((p) => p.id === currentProgressId.value);
            if (match) progressData.value = mapProgressData(match);
        },
        { deep: true },
    );

    watch(
        () => userStore.completed_progresses,
        (list) => {
            if (!currentProgressId.value) return;
            const match = list.find((p) => p.id === currentProgressId.value);
            if (match) progressData.value = mapProgressData(match);
        },
        { deep: true },
    );

    /**
     * Open the imported items or space panel without showing the import wizard.
     * Uses only the completed job's meta — never the live import_target, which
     * may belong to a different in-progress wizard. Hides the wizard without
     * clearing persisted state so an active session is not discarded unsaved.
     */
    async function openImportedDestination(progress) {
        const appStateStore = useAppState();
        const { hideImportWorkspaceUi, navigateToImportedWorkPanel } =
            useNavigateToImportedWorkPanel();
        const destination = getImportedWorkPanelNavigation(
            progress?.meta ?? {},
        );

        appStateStore.setWorkAsSelectedModule();
        appStateStore.goToWork();
        appStateStore.setImportState(null);
        hideImportWorkspaceUi();
        await navigateToImportedWorkPanel(destination);
    }

    /**
     * Open Work → Import Data for any job (completed, in-progress, or error).
     * Never skip the wizard to navigate the imported space.
     */
    async function openProgressDetail(progressId, type) {
        const appStateStore = useAppState();
        const workspaceStore = useWorkspace();
        const progress =
            userStore.initiated_progresses.find((p) => p.id === progressId) ||
            userStore.completed_progresses.find((p) => p.id === progressId) ||
            progressData.value;

        // if (shouldOpenImportedDestinationDirectly(progress)) {
        //     await openImportedDestination(progress);
        //     return;
        // }

        appStateStore.setWorkAsSelectedModule();
        appStateStore.setImportState({ progressId, type });
        workspaceStore.setImportStep(IMPORT_STEPS.IMPORT_DATA);
        openImportProgressDestinationPanel(
            workspaceStore,
            getImportProgressPanelDestination({
                progress,
                type,
                importTarget: workspaceStore.import_target,
            }),
        );
        workspaceStore.setImportWorkspaceMode(true);
        appStateStore.goToWork();
        await setProgressId(progressId);
    }

    return {
        currentProgressId,
        progressData,
        progressLoading,
        progressError,
        showProgressDetailView,
        isImportFinished,
        fetchProgressDetail,
        setupWebSocket,
        setProgressId,
        openProgressDetail,
        openImportedDestination,
    };
});
